import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box } from 'components/ui';
import Tab from 'components/Tab';
import LoyaltyProfileBanner from 'components/ProfileBanner/LoyaltyProfileBanner/LoyaltyProfileBanner';
import StickyLoyaltyProfileBanner from 'components/ProfileBanner/StickyLoyaltyProfileBanner';
import ComponentList from 'components/Content/ComponentList';
import contentConstants from 'constants/content';
import localeUtils from 'utils/LanguageLocale';
import Storage from 'utils/localStorage/Storage';
import userActions from 'actions/UserActions';
import BeautyInsiderTabbedEN from 'components/Content/BeautyInsider/locale/BeautyInsiderTabbed_en_US';
import BeautyInsiderTabbedFR from 'components/Content/BeautyInsider/locale/BeautyInsiderTabbed_fr_CA';
import HistoryTab from 'components/Content/BeautyInsider/BeautyInsiderHistory';
import beautyInsiderPageBindings from 'analytics/bindingMethods/pages/beautyInsider/beautyInsiderPageBindings';
import urlUtils from 'utils/Url';
import {
    colors, fontSizes, mediaQueries, space
} from 'style/config';
import { BEAUTY_INSIDER_TAB_KEYS, HISTORY_ROUTES } from 'components/Content/BeautyInsider/BeautyInsiderHistory/historyConstants';
import Empty from 'constants/empty';
import useIsSMUI from 'hooks/useIsSMUI';

const { CONTEXTS, COMPONENT_TYPES, RENDERING_TYPE } = contentConstants;
const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderTabbedEN, BeautyInsiderTabbedFR);
const PASSKEY_RESET_FLAG = 'passkeyResetRedirect';
const BENEFITS_RENDERING_TYPES = new Set([RENDERING_TYPE.BIHUB_FEATURED_BENEFITS, RENDERING_TYPE.BIHUB_CREDIT_CARD_HOLDER_SUMMARY]);

const isBenefitsCustomRendering = item => item?.type === COMPONENT_TYPES.CUSTOM_RENDERING && BENEFITS_RENDERING_TYPES.has(item.renderingType);

const addBenefitsViewStateToItems = (items = Empty.Array, benefitsViewState) =>
    items.map(item => (isBenefitsCustomRendering(item) ? { ...item, benefitsViewState } : item));

const BeautyInsiderTabbed = props => {
    const {
        fetchClientSummary,
        fetchBiRewardsPromise,
        isAtLeastRecognized = false,
        isBI = false,
        rewardsTabZone = Empty.Array,
        earnTabZone = Empty.Array,
        benefitsTabZone = Empty.Array,
        benefitsViewState = null,
        fetchCustomerLimit,
        historyProfile = Empty.Object,
        historyRegion = null,
        historyYearAtGlance = Empty.Object,
        historyRewardsRedeemed = Empty.Object,
        historyGroupedPoints = Empty.Object,
        user = Empty.Object,
        fetchHistoryRewards,
        fetchHistoryPoints,
        showPasskeyOptinModal,
        showPointsHistoryInfoModal
    } = props;

    const isSMUI = useIsSMUI();
    const isLogged = isAtLeastRecognized && isBI;
    const [activeTabKey, setActiveTabKey] = useState(BEAUTY_INSIDER_TAB_KEYS.REWARDS);

    const initialRewardsFetchedRef = useRef(false);
    const prevIsAtLeastRecognizedRef = useRef(null);
    const isFetchingRef = useRef(false);
    const fetchClientSummaryRef = useRef(fetchClientSummary);
    const fetchBiRewardsPromiseRef = useRef(fetchBiRewardsPromise);
    const initialIsAtLeastRecognizedRef = useRef(isAtLeastRecognized);

    // Fetch client summary on mount only.
    useEffect(() => {
        if (fetchClientSummaryRef.current) {
            fetchClientSummaryRef.current(true);
        }

        beautyInsiderPageBindings.setPageLoadAnalyticsHub();
    }, []);

    // Fetch BI rewards on mount only
    useEffect(() => {
        if (initialRewardsFetchedRef.current) {
            return;
        }

        initialRewardsFetchedRef.current = true;

        const fetchInitialRewards = async () => {
            try {
                if (fetchBiRewardsPromiseRef.current) {
                    isFetchingRef.current = true;
                    await fetchBiRewardsPromiseRef.current();
                }
            } catch (error) {
                Sephora.logger.error('BeautyInsiderTabbed: failed to fetch BI rewards', error);
            } finally {
                // Set flag only after fetch completes
                isFetchingRef.current = false;
                prevIsAtLeastRecognizedRef.current = initialIsAtLeastRecognizedRef.current;
            }
        };

        fetchInitialRewards();
    }, []);

    // Fetch BI rewards when user logs in
    useEffect(() => {
        // Skip if initial fetch hasn't completed yet
        if (isFetchingRef.current) {
            return;
        }

        const wasAnonymous = !prevIsAtLeastRecognizedRef.current;
        const isNowLoggedIn = isAtLeastRecognized;

        // Only fetch if user just logged in
        if (!isFetchingRef.current && fetchBiRewardsPromise && wasAnonymous && isNowLoggedIn) {
            isFetchingRef.current = true;

            fetchBiRewardsPromise()
                .catch(error => Sephora.logger.error('BeautyInsiderTabbed: failed to fetch BI rewards after login', error))
                .finally(() => {
                    isFetchingRef.current = false;
                });
        }

        // Update ref to prevent stale comparisons
        prevIsAtLeastRecognizedRef.current = isAtLeastRecognized;
    }, [fetchBiRewardsPromise, isAtLeastRecognized]);

    useEffect(() => {
        if (!historyProfile?.isBi || !user?.profileId) {
            return;
        }

        if (fetchHistoryRewards) {
            fetchHistoryRewards(user.profileId);
        }

        if (fetchHistoryPoints) {
            fetchHistoryPoints(user.profileId);
        }
    }, [fetchHistoryPoints, fetchHistoryRewards, historyProfile?.isBi, user?.profileId]);

    useEffect(() => {
        const isEmployeeDiscountTrackerEnabled = Sephora.configurationSettings?.ope?.IsEmployeeDiscountTrackerEnabled;

        if (!isEmployeeDiscountTrackerEnabled || !historyProfile?.isEmployee || !user?.profileId || !fetchCustomerLimit) {
            return;
        }

        fetchCustomerLimit();
    }, [fetchCustomerLimit, historyProfile?.isEmployee, user?.profileId]);

    // Passkey opt-in after password reset
    const hasHandledPasskeyFlagRef = useRef(false);

    useEffect(() => {
        if (hasHandledPasskeyFlagRef.current || !user?.isInitialized || !user.login) {
            return;
        }

        const resetFlag = Storage.session.getItem(PASSKEY_RESET_FLAG, false, true);

        if (!resetFlag) {
            return;
        }

        Storage.session.removeItem(PASSKEY_RESET_FLAG);
        hasHandledPasskeyFlagRef.current = true;

        const checkPasskeyOptin = async () => {
            const shouldShowPasskey = await userActions.shouldShowPasskeyOptin(
                { login: user.login },
                Sephora.configurationSettings?.passkey?.optInResetPwd?.isEnabled
            );

            if (shouldShowPasskey && showPasskeyOptinModal) {
                showPasskeyOptinModal({
                    isOpen: true,
                    email: user.login,
                    passkeyType: 'optinCreate',
                    placement: 'reset password'
                });
            }
        };

        checkPasskeyOptin();
    }, [user?.isInitialized, user?.login, showPasskeyOptinModal]);

    const frTabLabelCss = localeUtils.isFrench() ? styles.frTabLabel : null;

    const labelArray = [getText('tabRewards'), getText('tabEarnPoints'), getText('tabBenefits'), getText('tabHistory')];
    const keyArray = useMemo(
        () => [
            BEAUTY_INSIDER_TAB_KEYS.REWARDS,
            BEAUTY_INSIDER_TAB_KEYS.EARN_POINTS,
            BEAUTY_INSIDER_TAB_KEYS.BENEFITS,
            BEAUTY_INSIDER_TAB_KEYS.HISTORY
        ],
        []
    );
    const retryYearAtGlance = useCallback(() => fetchClientSummary && fetchClientSummary(true), [fetchClientSummary]);
    const retryRewardsRedeemed = useCallback(
        () => user?.profileId && fetchHistoryRewards && fetchHistoryRewards(user.profileId),
        [fetchHistoryRewards, user?.profileId]
    );
    const retryPointsHistory = useCallback(
        () => user?.profileId && fetchHistoryPoints && fetchHistoryPoints(user.profileId),
        [fetchHistoryPoints, user?.profileId]
    );
    const shopNow = useCallback(() => urlUtils.redirectTo(HISTORY_ROUTES.SHOP_NOW), []);
    const openPointsInfo = useCallback(() => showPointsHistoryInfoModal?.(), [showPointsHistoryInfoModal]);
    const historyActions = useMemo(
        () => ({
            openPointsInfo,
            retryPointsHistory,
            retryRewardsRedeemed,
            retryYearAtGlance,
            shopNow
        }),
        [openPointsInfo, retryPointsHistory, retryRewardsRedeemed, retryYearAtGlance, shopNow]
    );
    const benefitsTabZoneWithState = useMemo(
        () => addBenefitsViewStateToItems(benefitsTabZone, benefitsViewState),
        [benefitsTabZone, benefitsViewState]
    );
    const onTabChange = useCallback(key => {
        setActiveTabKey(key);
        beautyInsiderPageBindings.setPageLoadAnalyticsAsyncHub(key);
    }, []);

    const contentArray = useMemo(
        () => [
            <Box
                key='rewards'
                paddingTop={4}
            >
                <ComponentList
                    context={CONTEXTS.CONTAINER}
                    items={rewardsTabZone}
                    page='beautyInsider'
                    removeFirstItemMargin={true}
                />
            </Box>,
            <Box
                key='earn-points'
                paddingTop={4}
            >
                <ComponentList
                    context={CONTEXTS.CONTAINER}
                    items={earnTabZone}
                    page='beautyInsider'
                    removeFirstItemMargin={true}
                />
            </Box>,
            <Box
                key='benefits'
                paddingTop={4}
            >
                <ComponentList
                    context={CONTEXTS.CONTAINER}
                    items={benefitsTabZoneWithState}
                    page='beautyInsider'
                    removeFirstItemMargin={true}
                />
            </Box>,
            <Box key='history'>
                <HistoryTab
                    profile={historyProfile}
                    region={historyRegion}
                    yearAtGlance={historyYearAtGlance}
                    rewardsRedeemed={historyRewardsRedeemed}
                    groupedPoints={historyGroupedPoints}
                    actions={historyActions}
                />
            </Box>
        ],
        [
            benefitsTabZoneWithState,
            earnTabZone,
            historyActions,
            historyGroupedPoints,
            historyProfile,
            historyRegion,
            historyRewardsRedeemed,
            historyYearAtGlance,
            rewardsTabZone
        ]
    );

    const showStickyBanner = isSMUI && isLogged && activeTabKey === BEAUTY_INSIDER_TAB_KEYS.REWARDS;

    return (
        <Box>
            <Box css={styles.bannerWrapper}>
                <Box css={styles.blackBackground} />
                <LoyaltyProfileBanner {...props} />
            </Box>
            {showStickyBanner && <StickyLoyaltyProfileBanner />}
            <Box css={styles.tabWrapper}>
                <Tab
                    labelArray={labelArray}
                    contentArray={contentArray}
                    keyArray={keyArray}
                    tabLabelCss={frTabLabelCss}
                    onTabChange={onTabChange}
                />
            </Box>
        </Box>
    );
};

BeautyInsiderTabbed.propTypes = {
    benefitsTabZone: PropTypes.arrayOf(PropTypes.object),
    earnTabZone: PropTypes.arrayOf(PropTypes.object),
    fetchBiRewardsPromise: PropTypes.func,
    fetchClientSummary: PropTypes.func,
    fetchHistoryPoints: PropTypes.func,
    fetchHistoryRewards: PropTypes.func,
    historyGroupedPoints: PropTypes.object,
    historyProfile: PropTypes.object,
    historyRegion: PropTypes.string,
    historyRewardsRedeemed: PropTypes.object,
    historyYearAtGlance: PropTypes.object,
    isAtLeastRecognized: PropTypes.bool,
    isBI: PropTypes.bool,
    rewardsTabZone: PropTypes.arrayOf(PropTypes.object),
    showPasskeyOptinModal: PropTypes.func,
    showPointsHistoryInfoModal: PropTypes.func,
    user: PropTypes.shape({
        isInitialized: PropTypes.bool,
        login: PropTypes.string,
        profileId: PropTypes.string
    })
};

const styles = {
    bannerWrapper: {
        [mediaQueries.smMax]: {
            position: 'relative'
        }
    },
    blackBackground: {
        [mediaQueries.smMax]: {
            position: 'absolute',
            top: -space[5],
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100vw',
            height: space[7] * 2 + space[3],
            backgroundColor: colors.black,
            zIndex: 0
        }
    },
    tabWrapper: {
        [mediaQueries.smMax]: {
            marginTop: space[4]
        }
    },
    frTabLabel: {
        fontSize: fontSizes.xs,
        whiteSpace: 'normal',
        textAlign: 'center'
    }
};

export default wrapFunctionalComponent(BeautyInsiderTabbed, 'BeautyInsiderTabbed');
